import React from 'react';
import { motion } from 'framer-motion';
import { Star, ShoppingCart, Heart, ArrowRight } from 'lucide-react';
import { CartItem, Page } from '../App';
interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating: number;
  badge?: string;
  weight?: number;
}
const featuredProducts: Product[] = [{
  id: '1',
  name: 'Luxury Leather Tote',
  category: 'Fashion',
  price: 45000,
  originalPrice: 55000,
  image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&q=80&w=800',
  rating: 4.8,
  badge: 'Best Seller',
  weight: 0.8
}, {
  id: '2',
  name: 'Gold Chain Necklace',
  category: 'Accessories',
  price: 28500,
  image: 'https://images.unsplash.com/photo-1599643478518-17488fbbcd75?auto=format&fit=crop&q=80&w=800',
  rating: 4.9,
  weight: 0.1
}, {
  id: '3',
  name: 'Ankara Print Dress',
  category: 'Fashion',
  price: 18000,
  image: 'https://images.unsplash.com/photo-1585487000160-6ebcfceb0d03?auto=format&fit=crop&q=80&w=800',
  rating: 4.7,
  badge: 'New Arrival',
  weight: 0.5
}, {
  id: '4',
  name: 'Redmi Note 15 Pro',
  category: 'Electronics',
  price: 500000,
  image: 'https://cdn.magicpatterns.com/uploads/8cBVP6dQJouJukeTEWCVEZ/Redmi_Note_15_Pro.jpg',
  rating: 4.9,
  badge: 'Featured',
  weight: 0.2
}, {
  id: '5',
  name: 'Blender Pro 2000',
  category: 'Appliances',
  price: 32000,
  image: 'https://images.unsplash.com/photo-1570222094114-28a9d88a2d64?auto=format&fit=crop&q=80&w=800',
  rating: 4.6,
  weight: 2.5
}, {
  id: '6',
  name: 'Smart TV 43"',
  category: 'Electronics',
  price: 145000,
  image: 'https://images.unsplash.com/photo-1593784991095-a20506948430?auto=format&fit=crop&q=80&w=800',
  rating: 4.8,
  badge: 'Sale',
  weight: 8.0
}, {
  id: '7',
  name: 'Wireless Earbuds',
  category: 'Electronics',
  price: 25000,
  originalPrice: 35000,
  image: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?auto=format&fit=crop&q=80&w=800',
  rating: 4.5,
  weight: 0.1
}, {
  id: '8',
  name: 'Designer Sunglasses',
  category: 'Accessories',
  price: 15000,
  image: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&q=80&w=800',
  rating: 4.7,
  weight: 0.2
}];
interface ProductGridProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
}
export function ProductGrid({
  onAddToCart,
  onNavigate
}: ProductGridProps) {
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(amount);
  return <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-248">
      <div className="text-center mb-16" data-id="element-249">
        <h2 className="text-3xl md:text-4xl font-bold font-serif text-white mb-4" data-id="element-250">
          Featured <span className="border-b-4 border-[#D4AF37]" data-id="element-251">Products</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-252">
          Curated selection of premium fashion, home essentials, and electronics
          delivered to your doorstep.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-253">
        {featuredProducts.map((product, index) => <motion.div key={product.id} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: index * 0.1
      }} className="glass-card rounded-xl overflow-hidden group relative flex flex-col" data-id="element-254">
            <div className="relative h-64 overflow-hidden" data-id="element-255">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-256" />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" data-id="element-257" />
              {product.badge && <span className="absolute top-3 left-3 bg-[#D4AF37] text-black text-xs font-bold px-2 py-1 rounded" data-id="element-258">
                  {product.badge}
                </span>}
              <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-4 group-hover:translate-x-0" data-id="element-259">
                <button className="p-2 bg-white text-black rounded-full hover:bg-[#D4AF37] transition-colors" data-id="element-260">
                  <Heart className="w-4 h-4" data-id="element-261" />
                </button>
              </div>
            </div>

            <div className="p-5 flex-1 flex flex-col" data-id="element-262">
              <div className="flex justify-between items-start mb-2" data-id="element-263">
                <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-medium" data-id="element-264">
                  {product.category}
                </span>
                <div className="flex items-center gap-1" data-id="element-265">
                  <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" data-id="element-266" />
                  <span className="text-xs text-gray-400" data-id="element-267">
                    {product.rating}
                  </span>
                </div>
              </div>

              <h3 className="text-lg font-medium text-white mb-2 line-clamp-1" data-id="element-268">
                {product.name}
              </h3>

              <div className="mt-auto flex items-center justify-between" data-id="element-269">
                <div className="flex flex-col" data-id="element-270">
                  <span className="text-lg font-bold text-white" data-id="element-271">
                    {formatCurrency(product.price)}
                  </span>
                  {product.originalPrice && <span className="text-xs text-gray-500 line-through" data-id="element-272">
                      {formatCurrency(product.originalPrice)}
                    </span>}
                </div>
                <button onClick={() => onAddToCart(product)} className="p-3 bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white rounded-lg transition-colors group/btn" data-id="element-273">
                  <ShoppingCart className="w-5 h-5 transition-transform group-hover/btn:-rotate-12" data-id="element-274" />
                </button>
              </div>
            </div>
          </motion.div>)}
      </div>

      <div className="mt-16 text-center" data-id="element-275">
        <button onClick={() => onNavigate('fashion')} className="px-8 py-3 border border-white/20 text-white hover:bg-white hover:text-black transition-colors rounded-full font-medium inline-flex items-center gap-2" data-id="element-276">
          View All Products <ArrowRight className="w-4 h-4" data-id="element-277" />
        </button>
      </div>
    </section>;
}